/**
 * `SkillDetail` — pagina di dettaglio di una singola skill, raggiungibile dalla
 * vista "Livelli → Skill" cliccando il nome di una riga della matrice skill.
 *
 * Mostra:
 *  - il nome della skill, la sua descrizione (se presente) e il numero di
 *    persone che hanno almeno una valutazione su di essa;
 *  - una tabella delle persone con le **stesse colonne** della pagina delle
 *    skill (`SkillList` / `SkillAssessmentTable`): Livello (Self), Esperienza,
 *    Propensione, AI, Reviews, Score — qui in sola lettura, con in testa la
 *    colonna "Persona" (nome + reparto + link alla dashboard della persona).
 *
 * Dati: `GET /api/skills/:id/detail` → `SkillDetailDto` (rotta protetta lato API
 * da `requireLevelsAccess`, coerentemente con la sezione "Livelli"). La query è
 * abilitata solo quando l'`:skillId` è presente.
 */
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import type { Livello, SkillDetailDto, SkillHolderDto } from '../shared/dto';
import {
  EXPERIENCE_RANGE_LABELS,
  LIVELLO_VALUES,
  livelloRank,
} from '../shared/dto';
import { reviewsLevelRank } from '../components/SkillAssessmentTable';
import { LivelloBadge } from '../components/LivelloBadge';
import { ScoreBadge } from '../components/ScoreBadge';
import { CertificationCountBadge } from '../components/CertificationCountBadge';
import { propensionLabel } from '../components/PropensionSelect';
import { ABSENT_INDICATOR } from '../components/ScoreTriple';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Icon,
  Loading,
  PageHeader,
  type Column,
  type SortDirection,
} from '../components/ui';

/**
 * Converte un rango continuo `[0,5]` nel Livello più vicino, così da mostrarlo
 * come `LivelloBadge` nella colonna "Reviews". Coerente con la resa della
 * tabella skill (`SkillAssessmentTable`).
 */
function rankToLivello(rank: number): Livello {
  const index = Math.min(LIVELLO_VALUES.length - 1, Math.max(0, Math.round(rank)));
  return LIVELLO_VALUES[index];
}

/** Numero totale di recensioni (reviewer + manager) di cui tiene conto il rango. */
function reviewsCount(row: SkillHolderDto): number {
  return row.reviewerCount + row.managerCount;
}

/** Cella "Reviews": Livello aggregato reviewer/manager + numero di recensioni. */
function reviewsCell(row: SkillHolderDto) {
  const rank = reviewsLevelRank(row);
  if (rank === null) {
    return (
      <span className="skill-list__absent" aria-label="Reviews: nessun voto">
        {ABSENT_INDICATOR}
      </span>
    );
  }
  const count = reviewsCount(row);
  return (
    <span className="skill-list__reviews">
      <LivelloBadge livello={rankToLivello(rank)} />
      <span
        className="skill-list__reviews-count"
        title={`${count} ${count === 1 ? 'recensione' : 'recensioni'}`}
        aria-label={`${count} ${count === 1 ? 'recensione' : 'recensioni'}`}
      >
        {count}
      </span>
    </span>
  );
}

/** Cella "Propensione": barra `0..10` con etichetta, o "—" se assente. */
function propensionCell(value: number | null) {
  if (value === null) {
    return (
      <span className="skill-list__absent" aria-label="Propensione: nessuna preferenza">
        {ABSENT_INDICATOR}
      </span>
    );
  }
  const percent = (value / 10) * 100;
  const text = propensionLabel(value);
  return (
    <span
      className="skill-list__propension-bar"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={10}
      aria-valuenow={value}
      aria-valuetext={text}
      aria-label="Propensione"
      title={`${text} (${value}/10)`}
    >
      <span className="skill-list__propension-bar-fill" style={{ width: `${percent}%` }} />
    </span>
  );
}

/** Cella "Score": badge colorato + coccarda certificazioni, o "—" se assente. */
function scoreCell(row: SkillHolderDto) {
  const certBadge = (
    <CertificationCountBadge count={row.certificationCount} skillName={row.skillName} />
  );
  if (row.score === null) {
    return (
      <span className="skill-list__score-cell">
        <span className="skill-list__absent" aria-label="Score: assente">
          {ABSENT_INDICATOR}
        </span>
        {certBadge}
      </span>
    );
  }
  return (
    <span className="skill-list__score-cell">
      <ScoreBadge value={row.score} label="Score" />
      {certBadge}
    </span>
  );
}

/** Comparatore per la colonna "Livello (Self)": assenti (`null`) in coda. */
function compareLivello(a: SkillHolderDto, b: SkillHolderDto, direction: SortDirection): number {
  const la = a.livelloSelf;
  const lb = b.livelloSelf;
  if (la === null && lb === null) return 0;
  if (la === null) return 1;
  if (lb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (livelloRank(la) - livelloRank(lb));
}

/** Comparatore per la colonna "AI": assenti (`null`) in coda. */
function compareAiLevel(a: SkillHolderDto, b: SkillHolderDto, direction: SortDirection): number {
  const la = a.aiLevel;
  const lb = b.aiLevel;
  if (la === null && lb === null) return 0;
  if (la === null) return 1;
  if (lb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (livelloRank(la) - livelloRank(lb));
}

/** Comparatore per la colonna "Propensione": assenti (`null`) in coda. */
function compareInterest(a: SkillHolderDto, b: SkillHolderDto, direction: SortDirection): number {
  const va = a.selfInterest;
  const vb = b.selfInterest;
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/** Comparatore per la colonna "Reviews": assenti (`null`) in coda. */
function compareReviews(a: SkillHolderDto, b: SkillHolderDto, direction: SortDirection): number {
  const va = reviewsLevelRank(a);
  const vb = reviewsLevelRank(b);
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

/** Comparatore per la colonna "Score": assenti (`null`) in coda. */
function compareScore(a: SkillHolderDto, b: SkillHolderDto, direction: SortDirection): number {
  const va = a.score;
  const vb = b.score;
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const sign = direction === 'desc' ? -1 : 1;
  return sign * (va - vb);
}

export default function SkillDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { skillId } = useParams<{ skillId: string }>();

  const { data, isLoading, isError, error } = useQuery<SkillDetailDto>({
    queryKey: ['skills', 'detail', skillId],
    queryFn: () =>
      api.get<SkillDetailDto>(
        `/api/skills/${encodeURIComponent(skillId ?? '')}/detail`,
      ),
    enabled: skillId !== undefined && skillId !== '',
  });

  const backToLevels = () => {
    void navigate('/insights?view=skills');
  };

  const columns = useMemo<Column<SkillHolderDto>[]>(
    () => [
      {
        key: 'displayName',
        header: 'Persona',
        sortable: true,
        sortAccessor: (row) => row.displayName.toLowerCase(),
        render: (row) => (
          <div className="coverage-name">
            <Link
              to={`/insights/team/${encodeURIComponent(row.userId)}`}
              className="coverage-name__link"
              aria-label={`Apri la dashboard di ${row.displayName}`}
            >
              {row.displayName}
              <Icon name="external" size={14} className="coverage-name__link-icon" />
            </Link>
            {row.departmentName !== null && (
              <Badge tone="neutral">{row.departmentName}</Badge>
            )}
          </div>
        ),
      },
      {
        key: 'livello',
        header: 'Livello',
        sortable: true,
        sortComparator: compareLivello,
        render: (row) => (
          <span className="skill-list__level-cell">
            <LivelloBadge livello={row.livelloSelf} />
          </span>
        ),
        className: 'skill-list__level-col',
      },
      {
        key: 'experience',
        header: 'Esperienza',
        sortable: true,
        sortComparator: (a, b, direction) => {
          const rankOf = (v: SkillHolderDto['yearsOfExperience']) =>
            v === null ? -1 : Object.keys(EXPERIENCE_RANGE_LABELS).indexOf(v);
          const ra = a.yearsOfExperience;
          const rb = b.yearsOfExperience;
          if (ra === null && rb === null) return 0;
          if (ra === null) return 1;
          if (rb === null) return -1;
          const sign = direction === 'desc' ? -1 : 1;
          return sign * (rankOf(ra) - rankOf(rb));
        },
        render: (row) =>
          row.yearsOfExperience !== null ? (
            <span className="skill-list__years">
              {EXPERIENCE_RANGE_LABELS[row.yearsOfExperience]}
            </span>
          ) : (
            <span className="skill-list__absent" aria-label="Esperienza: non dichiarata">
              {ABSENT_INDICATOR}
            </span>
          ),
        className: 'skill-list__experience-col',
      },
      {
        key: 'interest',
        header: 'Propensione',
        sortable: true,
        sortComparator: compareInterest,
        render: (row) => propensionCell(row.selfInterest),
        className: 'skill-list__score-col skill-list__score-col--self',
      },
      {
        key: 'aiLevel',
        header: 'AI',
        sortable: true,
        sortComparator: compareAiLevel,
        render: (row) => (
          <span className="skill-list__level-cell">
            <LivelloBadge livello={row.aiLevel} />
          </span>
        ),
        className: 'skill-list__score-col',
      },
      {
        key: 'reviews',
        header: 'Reviews',
        sortable: true,
        sortComparator: compareReviews,
        render: (row) => reviewsCell(row),
        className: 'skill-list__score-col skill-list__reviews-col',
      },
      {
        key: 'score',
        header: 'Score',
        sortable: true,
        sortComparator: compareScore,
        render: (row) => scoreCell(row),
        className: 'skill-list__score-col',
      },
    ],
    [],
  );

  if (isLoading) {
    return <Loading>Caricamento skill…</Loading>;
  }

  if (isError || data === undefined) {
    const message =
      error instanceof Error ? error.message : 'Impossibile caricare la skill.';
    return (
      <section className="skill-detail" aria-labelledby="skill-detail-title">
        <PageHeader
          title="Dettaglio skill"
          titleId="skill-detail-title"
          actions={
            <Button type="button" variant="ghost" onClick={backToLevels}>
              ← Torna alle skill
            </Button>
          }
        />
        <Alert tone="error">{message}</Alert>
      </section>
    );
  }

  return (
    <section className="skill-detail" aria-labelledby="skill-detail-title">
      <PageHeader
        title="Dettaglio skill"
        titleId="skill-detail-title"
        subtitle="Chi la possiede e i relativi voti (Self, Esperienza, Propensione, AI, Reviews, Score)."
        actions={
          <Button type="button" variant="ghost" onClick={backToLevels}>
            ← Torna alle skill
          </Button>
        }
      />

      <div className="skill-detail__heading">
        <h2 className="skill-detail__name">{data.name}</h2>
        {data.description !== undefined && (
          <p className="skill-detail__description">{data.description}</p>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={data.holders}
        rowKey={(row) => row.userId}
        defaultSort={{ key: 'score', direction: 'desc' }}
        emptyMessage="Nessuna persona ha ancora una valutazione su questa skill."
        pageSize={15}
      />
    </section>
  );
}
